/*
 * Copyright (c) 2023 by N-Ach (Pvt) Ltd
 *
 * ╔═╗ ╔╗    ╔═══╗  ╔╗
 * ║║╚╗║║    ║╔═╗║  ║║
 * ║╔╗╚╝║    ║║ ║╠══╣╚═╗
 * ║║╚╗║║╔══╗║╚═╝║╔═╣╔╗║
 * ║║ ║║║╚══╝║╔═╗║╚═╣║║║
 * ╚╝ ╚═╝    ╚╝ ╚╩══╩╝╚╝
 *
 * All rights reserved.
 *
 * This software is the confidential and proprietary information
 * of N-Ach (Pvt) Ltd. You shall not disclose such Confidential
 * Information and shall use it only in accordance with the terms
 * of the license agreement you entered into with N-Ach (Pvt) Ltd.
 *
 *
 * @Author Dilhari Jayathunga
 * @Date 31/3/2023
 */

import { Close } from "@mui/icons-material";
import { Button, IconButton, OutlinedInput, Typography } from "@mui/material";
import { FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import ImageUploader from "../Shared/ImageUploader";
import { useState } from "react";
import { toast } from "react-toastify";
import CategoryList from "../Shared/CategoryList";
import { useDispatch } from "react-redux";
import { openAddCategoryModal } from "../../Store/Slices/categorySlice";
import styles from "./AddCategory.module.css";
import { useCreateCategoryMutation } from "../../Store/Services/CategoryService";
import { getAllTopCategories } from "../../Store/Services/CategoryService";
import { useDeleteCategoryFolderMutation } from "../../Store/Services/ImageService";
import AttributeTable from "./attributeTable";

const schema = yup.object().shape({
  catName: yup.string().required(),
  catDisplay: yup.string().required(),
  attributes: yup.array().of(
    yup.object().shape({
      attributeIndex: yup.string().required(),
      attributeName: yup.string().required(),
      pickerType: yup.string().required(),
      mandatory: yup.boolean().required(),
      attributeValues: yup.array().of(
        yup.object().shape({
          value: yup.string().required(),
        }).required()
      ).required(),
    }).required()
  ).required(),
});


const AddCategory = ({ handleClose }) => {
  const dispatch = useDispatch();
  const methods = useForm({
    resolver: yupResolver(schema),
  });

  const [createCategory] = useCreateCategoryMutation();
  const [deleteImgFolder] = useDeleteCategoryFolderMutation();

  const [images, setImages] = useState([]);
  const [catName, setCatName] = useState("");
  const [openCatList, setOpenCatList] = useState(false);
  const [selectedCat, setSelectedCat] = useState(null);

  const onSubmit = async (data) => {
    const formattedAttributes = data.attributes.map((attr, index) => ({
      attributeId: attr.attributeId || null,
      attributeIndex: index + 1,
      attributeName: attr.attributeName,
      attributeTypeId: attr.pickerType,
      isMandatory: attr.mandatory ? 1 : 0,
      catAttributeValues: attr.attributeValues.map(val => ({
        attrValId: val.attrValId || null,
        catAttributeValue: val.value
      })),
    }));

    const dataSet = {
      catName: data.catName,
      catDisplay: data.catDisplay,
      catAttribute: formattedAttributes,
      parentCat: selectedCat,
      imageList: images.toString(),
    };

    if (images.length !== 0) {
      try {
        const addCategory = await createCategory(dataSet).unwrap();
        dispatch(openAddCategoryModal(false));
        toast.success("Category successfully added!", {
          position: "bottom-right",
        });
      } catch (err) {
        console.error("Failed to save the category: ", err);
        toast.error("Error, something went wrong!", {
          position: "bottom-right",
        });
      }
      handleClose();
    }
  };

  const closeDialog = async () => {
    await deleteImgFolder().unwrap();
    handleClose();
  };

  return (
    <div>
      <div className={styles.headingContainer}>
        <Typography fontWeight={600} variant={"h6"}>
          Category Creation
        </Typography>
        <IconButton className={styles.closeButton} onClick={handleClose}>
          <Close />
        </IconButton>
      </div>
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(onSubmit)}>
          <div className={styles.formItem}>
            <label className={styles.formLabel}>Name:</label>
            {methods.formState.errors.catName && (
              <span className={styles.validation}> *</span>
            )}
            <OutlinedInput
              {...methods.register("catName")}
              className={styles.formInput}
              placeholder="Enter Category Name"
              size="small"
              fullWidth
            />
          </div>

          <div className={styles.formItem}>
            <label className={styles.formLabel}>Display Name:</label>
            {methods.formState.errors.catDisplay && (
              <span className={styles.validation}> *</span>
            )}
            <OutlinedInput
              {...methods.register("catDisplay")}
              className={styles.formInput}
              placeholder="Enter Display Name"
              size="small"
              fullWidth
            />
          </div>

          <div className={styles.formItem}>
            <label className={styles.formLabel}>Category Description:</label>
            <OutlinedInput
              {...methods.register("catDesc")}
              className={styles.formInput}
              placeholder="Enter Display Name"
              size="small"
              fullWidth
              multiline
            />
          </div>

          <div className={styles.formItem}>
            <label className={styles.formLabel}>Parent Category:</label>
            <OutlinedInput
              {...methods.register("parentCat")}
              className={styles.formInput}
              placeholder="Enter Parent Category"
              size="small"
              onClick={() => setOpenCatList(true)}
              autoComplete="off"
              fullWidth
              value={catName}
            />
          </div>

          <div style={{ marginTop: "1rem" }}>
            <label style={{ fontWeight: 600 }}>Attributes:</label>
            <hr />
            <AttributeTable />
          </div>

          <div className={styles.formItem}>
            <label className={styles.formLabel}>Images:</label>
            {images.length === 0 && (
              <span className={styles.validation}> *</span>
            )}
            <ImageUploader images={images} setImages={setImages} from="cat" />
          </div>

          <div className={styles.buttonContainer}>
            <Button
              type="submit"
              className={styles.submitButton}
              variant="contained"
            >
              Save
            </Button>
          </div>
        </form>
      </FormProvider>
      <CategoryList
        open={openCatList}
        onClose={() => setOpenCatList(false)}
        setSelectedCat={setSelectedCat}
        selectedCat={selectedCat}
        setCatName={setCatName}
      />
    </div>
  );
};

export default AddCategory;
